import React, { useEffect, useRef } from 'react';
import { useAdmin } from '../context/AdminContext';
import { useNavigate } from 'react-router-dom';
import { Users, Trophy, Search, Filter, ChevronRight, UserCheck, Bot, UserX, Loader2, Download } from 'lucide-react';
import { getAvatarGradient } from '../utils/avatarColor';
import AdminNav from './AdminNav';

// How a Premium / Pro user is linked to the paying server (API field paidVia).
const PAID_VIA = {
  owner: { label: 'Owner', title: 'Owns the paying server', className: 'border-emerald-500/20 text-emerald-300 bg-emerald-500/5' },
  installer: { label: 'Installer', title: 'Added the bot to the paying server (not the owner)', className: 'border-sky-500/20 text-sky-300 bg-sky-500/5' },
  member: { label: 'Member', title: 'Uses translation in the paying server (neither owner nor installer)', className: 'border-slate-500/20 text-slate-300 bg-slate-500/5' },
};

export default function Dashboard() {
  const {
    can, users, toggleStatus, pagination, usersStats, loadingMore, hasMoreUsers, loadMoreUsers,
    userSearch, setUserSearch, userPlan, setUserPlan, userFilter, setUserFilter,
  } = useAdmin();
  const navigate = useNavigate();

  const sentinelRef = useRef(null);

  // Observe the sentinel at the bottom of the table and load the next page when it scrolls
  // into view. Query caching (staleTime + queryKey) lives in AdminContext via TanStack Query -
  // changing userSearch/userPlan there automatically starts a fresh paged query from page 1.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreUsers();
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMoreUsers]);

  // Platform-wide KPIs come from the standalone GET /users/stats call, not the loaded pages
  const totalUsers = usersStats.totalUsers || pagination.total || users.length;
  const installedUsersCount = usersStats.installedUsers;
  const freeUsersCount = usersStats.freeTierUsers;
  const subscriptionUsersCount = usersStats.subscriptionTiers.total;

  // With backend filtering, users from state is already filtered
  const filteredUsers = users;



  return (
    <div className="min-h-screen bg-[#070a13] text-slate-200 flex flex-col font-sans">

      {/* 1. Header / Navbar */}
      <AdminNav />

      {/* Main Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-6 space-y-6">

        {/* Page Title Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight">Users Analytics Directory</h1>
            <p className="text-xs text-slate-400 mt-1">Audit usage indexes, plan structures, and grant system tokens to developers.</p>
          </div>
        </div>

        {/* 2. KPI Analytical Grid Stats Strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">

          {/* Card 1: Total Users */}
          <div className="glass-panel p-5 rounded-2xl border-white/[0.05] relative overflow-hidden group hover:border-indigo-500/20 transition-all duration-300">
            <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-500/5 rounded-full blur-xl pointer-events-none" />
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Total Users</span>
              <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/10">
                <Users className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-black text-white tracking-tight">{totalUsers}</span>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mt-1">Aggregated platform developer nodes</span>
            </div>
          </div>

          {/* Card 2: Installed Users */}
          <div className="glass-panel p-5 rounded-2xl border-white/[0.05] relative overflow-hidden group hover:border-emerald-500/20 transition-all duration-300">
            <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/5 rounded-full blur-xl pointer-events-none" />
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Installed Users</span>
              <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/10">
                <Download className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-black text-white tracking-tight">{installedUsersCount}</span>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mt-1">Completed bot install flow</span>
            </div>
          </div>

          {/* Card 3: Free Users */}
          <div className="glass-panel p-5 rounded-2xl border-white/[0.05] relative overflow-hidden group hover:border-cyan-500/20 transition-all duration-300">
            <div className="absolute top-0 right-0 w-20 h-20 bg-cyan-500/5 rounded-full blur-xl pointer-events-none" />
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Free Tier Users</span>
              <div className="p-2.5 rounded-xl bg-cyan-500/10 text-cyan-400 border border-cyan-500/10">
                <UserCheck className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-black text-white tracking-tight">{freeUsersCount}</span>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mt-1">Standard sandbox tier limits</span>
            </div>
          </div>

          {/* Card 4: Subscription Users */}
          <div className="glass-panel p-5 rounded-2xl border-white/[0.05] relative overflow-hidden group hover:border-amber-500/20 transition-all duration-300">
            <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/5 rounded-full blur-xl pointer-events-none" />
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Subscription Tiers</span>
              <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/10">
                <Trophy className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-black text-white tracking-tight">{subscriptionUsersCount}</span>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mt-1">
                Paid servers &middot; Premium {usersStats.subscriptionTiers.premium || 0} &middot; Pro {usersStats.subscriptionTiers.pro || 0}
              </span>
            </div>
          </div>

        </div>

        {/* 3. Filtering and Searching Widgets */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl bg-slate-900/30 border border-white/5">
          {/* Search Inputs */}
          <div className="relative flex-1 max-w-md group">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-indigo-400 transition-colors">
              <Search className="w-4 h-4" />
            </span>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="w-full bg-slate-950 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all"
              placeholder="Search developers by name, email or Discord ID..."
            />
          </div>

          {/* Subscription Tier + Activity Filters */}
          <div className="flex flex-wrap items-center gap-3 self-end md:self-auto">
            <span className="text-xs text-slate-400 font-semibold flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-indigo-400" />
              Subscription Layer:
            </span>
            <div className="flex gap-1.5 bg-slate-950 p-1 rounded-xl border border-white/5">
              {['All', 'Free', 'Premium', 'Pro'].map((plan) => (
                <button
                  key={plan}
                  onClick={() => setUserPlan(plan)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${userPlan === plan
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm shadow-indigo-500/10'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                    }`}
                >
                  {plan}
                </button>
              ))}
            </div>

            <div className="flex gap-1.5 bg-slate-950 p-1 rounded-xl border border-white/5">
              {[
                { value: '', label: 'All' },
                { value: 'active', label: 'Active' },
              ].map(({ value, label }) => (
                <button
                  key={label}
                  onClick={() => setUserFilter(value)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${userFilter === value
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm shadow-indigo-500/10'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                    }`}
                  title={value === 'active' ? 'Used the bot in the last 7 days' : 'No activity filter'}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 4. Directory Data Table Grid */}
        <div className="glass-panel rounded-2xl overflow-hidden shadow-2xl relative border-white/[0.05]">

          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-left">
              <thead>
                <tr className="border-b border-white/[0.06] bg-slate-900/40 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                  <th className="py-4 px-6 font-bold">Developer</th>
                  <th className="py-4 px-6 font-bold">Discord Servers</th>
                  <th className="py-4 px-6 font-bold">Came From</th>
                  {/* <th className="py-4 px-6 font-bold">Status</th> */}
                  <th className="py-4 px-6 font-bold">Subscription Spent</th>
                  <th className="py-4 px-6 text-right font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const serversCount = user.serverCount || 0;
                    return (
                      <tr
                        key={user.id}
                        onClick={() => navigate(`/user/${user.id}`)}
                        className="group cursor-pointer transition-all duration-150 hover:bg-white/[0.02]"
                      >
                        {/* Column 1: Persona (Name & Discord ID) */}
                        <td className="py-4 px-6 flex items-center gap-3.5">
                          <div className={`w-9 h-9 rounded-full bg-gradient-to-tr ${getAvatarGradient(user.id)} flex items-center justify-center font-extrabold text-white text-xs shadow-inner shrink-0 transform group-hover:scale-105 transition-transform`}>
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div className="space-y-0.5 overflow-hidden">
                            <span className="text-xs font-bold text-white group-hover:text-indigo-300 transition-colors block truncate">
                              {user.name}
                            </span>
                            <span className="text-[10px] text-slate-500 block truncate font-medium font-mono">
                              {user.id}
                            </span>
                            {user.email && (
                              <span className="text-[10px] text-slate-400 block truncate font-medium" title={user.emailVerified ? 'Verified by Discord' : 'Not verified by Discord'}>
                                {user.email}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Column 2: Connected Discord Servers Count */}
                        <td className="py-4 px-6 font-semibold text-xs">
                          <span className="text-slate-100 font-mono bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 px-3 py-1 rounded-full inline-flex items-center gap-1.5 shadow-sm">
                            <Bot className="w-3.5 h-3.5" />
                            {serversCount} {serversCount === 1 ? 'Server' : 'Servers'}
                          </span>
                        </td>

                        {/* Column: acquisition source (first touch of the user's journey) */}
                        <td className="py-4 px-6 text-xs">
                          {user.firstTouch ? (
                            <div className="flex flex-col gap-0.5 max-w-[180px]">
                              <span className={`font-bold truncate ${user.firstTouch.source === 'direct' ? 'text-slate-400' : 'text-indigo-300'}`}>
                                {user.firstTouch.source}
                                <span className="text-slate-500 font-medium"> / {user.firstTouch.medium}</span>
                              </span>
                              {user.firstTouch.campaign && (
                                <span className="text-[10px] text-slate-500 truncate" title={user.firstTouch.campaign}>
                                  {user.firstTouch.campaign}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-600" title="Joined before tracking started">—</span>
                          )}
                        </td>

                        {/* Column 3: Status state dot badge */}
                        {/* <td className="py-4 px-6">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] tracking-wider uppercase font-extrabold rounded-full border ${user.status === 'Active'
                            ? 'border-emerald-500/20 text-emerald-400 bg-emerald-500/5'
                            : 'border-rose-500/20 text-rose-400 bg-rose-500/5'
                            }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'Active' ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`} />
                            {user.status}
                          </span>
                        </td> */}

                        {/* Column 4: Subscription Spent */}
                        <td className="py-4 px-6">
                          <div className="flex flex-col gap-1.5 justify-center">
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-flex items-center w-fit px-2 py-0.5 text-[9px] tracking-wider uppercase font-extrabold rounded border ${user.tier === 'Pro'
                                ? 'border-amber-500/20 text-amber-400 bg-amber-500/5'
                                : user.tier === 'Premium'
                                  ? 'border-purple-500/20 text-purple-400 bg-purple-500/5'
                                  : 'border-slate-500/20 text-slate-400 bg-slate-500/5'
                                }`}>
                                {(user.tier || 'Free').toUpperCase()}
                              </span>
                              {user.paidVia && (
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold rounded border ${PAID_VIA[user.paidVia]?.className || PAID_VIA.member.className}`}
                                  title={PAID_VIA[user.paidVia]?.title}
                                >
                                  {PAID_VIA[user.paidVia]?.label || user.paidVia}
                                </span>
                              )}
                            </div>
                            <span className="text-slate-300 font-mono text-xs font-bold">
                              ${(user.subscriptionSpent || 0).toFixed(2)}
                            </span>
                          </div>
                        </td>

                        {/* Column 5: Elegant actions row */}
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-2.5">
                            {!can('users_edit') ? null : user.status === 'Active' ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleStatus(user.id);
                                }}
                                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 transition-all inline-flex items-center gap-1.5 shadow-md shadow-rose-500/5 hover:border-rose-500/50 active:scale-95"
                                title="Block Developer Account"
                              >
                                <UserX className="w-3.5 h-3.5" />
                                Block
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleStatus(user.id);
                                }}
                                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 transition-all inline-flex items-center gap-1.5 shadow-md shadow-emerald-500/5 hover:border-emerald-500/50 active:scale-95"
                                title="Unblock Developer Account"
                              >
                                <UserCheck className="w-3.5 h-3.5" />
                                Unblock
                              </button>
                            )}

                            <button
                              className="px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 transition-all inline-flex items-center gap-1.5 shadow-md shadow-indigo-500/5 hover:border-indigo-500/50 hover:scale-[1.02] active:scale-[0.98]"
                              title="Inspect Metrics"
                            >
                              Manage
                              <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                            </button>
                          </div>
                        </td>

                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="py-12 px-6 text-center text-slate-500">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Users className="w-8 h-8 text-slate-600 animate-pulse" />
                        <span className="text-sm font-semibold">No developers match filter parameters</span>
                        <span className="text-xs">Refine your search term or selection tier query.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Scroll sentinel - triggers loading the next page when it enters the viewport */}
          <div ref={sentinelRef} />

          {/* Table Footer index indicators */}
          <div className="px-6 py-4 border-t border-white/5 bg-slate-900/20 text-[10px] text-slate-500 font-semibold tracking-wider uppercase flex flex-col items-center justify-center gap-2">
            {loadingMore ? (
              <span className="flex items-center gap-2 text-indigo-300 normal-case text-xs">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading more developers...
              </span>
            ) : !hasMoreUsers && filteredUsers.length > 0 ? (
              <span>You've reached the end — {totalUsers} nodes listed</span>
            ) : (
              <span>Showing {filteredUsers.length} of {totalUsers} nodes &middot; scroll for more</span>
            )}
          </div>

        </div>

      </main>
    </div>
  );
}
